import * as Yup from 'yup';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { MainLayoutComponent } from 'components/LayoutComponent/SidebarLayout/MainLayoutComponent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import ButtonLoading from '@mui/lab/LoadingButton';
import { Box, Radio, RadioGroup, FormControlLabel } from '@mui/material';

import { useFormik } from 'formik';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { AutoCompleteWithType } from 'types/api/Tenant/AROKMS/CustomRuleTypes';
import { useNavigate } from 'react-router-dom';
import { PATH_CONSTANT } from 'constant/PathConstant';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import Editor from './components/Editor';
import VariableTree from './components/VariableTree';
import { mergeApiVariablesWithExisting } from './components/rowenaVariables';
import {
  findOrphanReferences,
  flattenAvailableVariables,
  formatOrphanList,
  pickDefaultFormat,
  pickDefaultOption,
} from './components/rowenaFormat';
import {
  useCreateRowenaContent,
  useGetRowenaAvailableVariables,
  useGetRowenaContentPageListDropdown,
  useGetRowenaContentPageTypesDropdown,
} from 'services/v1/SystemTenant/RowenaAI/ContentDefinition/RowenaContentDefinitionService';
import { RichTextEditorRef } from 'mui-tiptap';
import {
  RequestCreateRowenaContent,
  RowenaAvailableVariable,
} from 'types/api/SystemTenant/RowenaAI/RowenaContentTypes';
import { toast } from 'react-toastify';

const autocompleteStyles = {
  width: '55%',
  '& .MuiOutlinedInput-root': {
    padding: '1px 2px',
  },
  '& .MuiAutocomplete-popper': {
    fontSize: '10px',
  },
};
const textInputStyles = {
  '& .MuiOutlinedInput': {
    padding: '1px 2px',
  },
};

export interface FunctionDefinitionParamFields {
  name: string;
  fieldTitle: string;
  fieldSubtitle: string;
  fieldType: 'text' | 'select' | 'autocomplete';
  placeholder?: string;
  ruleTypes: string[];
  enforceUniqueSelection: boolean;
  fieldOptions?: AutoCompleteWithType[];
  isRequired?: boolean;
}

export interface CreateRowenaContentDefinitionFormikInitialValues {
  id: string | null;
  content: string | null;
  name: string | null;
  page: AutoCompleteItem | null;
  pageType: AutoCompleteItem | null;
  variables: string | null;
}
export const createRowenaContentDefinitionFormikInitialValues: CreateRowenaContentDefinitionFormikInitialValues = {
  id: null,
  content: null,
  name: null,
  page: null,
  pageType: null,
  variables: null,
};

export const createRowenaCotnentValidationSchema = Yup.object().shape({
  name: Yup.string().required('This field is required').nullable(),
  page: Yup.object().required('This field is required').nullable(),
  pageType: Yup.object().required('This field is required').nullable(),
});

type TemplateKey = 'simple' | 'multiNoToc' | 'multiWithToc';

const CONTENT_TEMPLATES: Record<TemplateKey, { label: string; description: string; html: string }> = {
  simple: {
    label: 'Single message',
    description: 'One block of content with no navigation or table of contents.',
    html: '<p>Write your message here...</p>',
  },
  multiNoToc: {
    label: 'Multi-page (no table of contents)',
    description: 'Pages separated by horizontal rules. Users navigate with Previous / Next.',
    html:
      '<hr>' +
      '<h2>Page 1</h2>' +
      '<p>Write the content for the first page...</p>' +
      '<hr>' +
      '<h2>Page 2</h2>' +
      '<p>Write the content for the second page...</p>',
  },
  multiWithToc: {
    label: 'Multi-page with table of contents',
    description: 'Preamble + clickable topic list that stays visible on every page. Start each page with a heading.',
    html:
      '<h2>Welcome</h2>' +
      '<p>Use the topic list below to navigate through this guide.</p>' +
      '<hr>' +
      '<h2>Introduction</h2>' +
      '<p>Write the introduction content...</p>' +
      '<hr>' +
      '<h2>Getting Started</h2>' +
      '<p>Write the getting-started content...</p>' +
      '<hr>' +
      '<h2>Advanced Topics</h2>' +
      '<p>Write the advanced-topics content...</p>',
  },
};

export default function CreateFunctionDefinitionPage() {
  const { t } = useTranslation();
  const editorRef = React.useRef<RichTextEditorRef>(null);
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = React.useState<TemplateKey | ''>('');

  const applyTemplate = (key: TemplateKey) => {
    const editor = editorRef.current?.editor;
    if (!editor) return;
    if (editor.getText().trim()) {
      if (!window.confirm('Replace the current content with this template? You can undo with Ctrl+Z.')) return;
    }
    editor.commands.setContent(CONTENT_TEMPLATES[key].html, true);
    setSelectedTemplate(key);
  };

  const insertVariable = (variable: RowenaAvailableVariable) => {
    const editor = editorRef.current?.editor;
    if (!editor) return;
    const format = pickDefaultFormat(variable);
    const option = pickDefaultOption(variable);
    editor
      .chain()
      .focus()
      .insertContent([
        { type: 'mention', attrs: { id: variable.name, label: variable.name, format, option } },
        { type: 'text', text: ' ' },
      ])
      .run();
  };

  const formik = useFormik({
    initialValues: createRowenaContentDefinitionFormikInitialValues,
    validationSchema: createRowenaCotnentValidationSchema,
    onSubmit: () => {},
  });
  const { data: rowenaPageTypeOptions, isLoading: isLoadingCubeOptions } = useGetRowenaContentPageTypesDropdown();

  const pageTypeValue = formik.values.pageType?.value?.toString();

  const { data: rowenaPageOptions, isLoading: isLoadingCubeLayerOptions } =
    useGetRowenaContentPageListDropdown(pageTypeValue);

  const { data: availableVariables, isLoading: isLoadingAvailableVariables } =
    useGetRowenaAvailableVariables(pageTypeValue);

  const variablesByName = React.useMemo(
    () =>
      availableVariables?.data?.groups ? flattenAvailableVariables(availableVariables.data.groups) : undefined,
    [availableVariables?.data?.groups],
  );

  const { mutate: createRowenaContent, isLoading: isInserting } = useCreateRowenaContent();

  const handleOnSubmit = () => {
    const editor = editorRef.current?.editor;
    if (editor && variablesByName) {
      const orphans = findOrphanReferences(editor.state.doc, variablesByName);
      if (orphans.length > 0) {
        // eslint-disable-next-line no-console
        console.warn('[Rowena] Orphan variable references blocking save:', orphans);
        toast.error(
          `Cannot save: these variables are not available for the selected page type — ${formatOrphanList(
            orphans,
          )}. Remove them or switch the page type. (See browser console for details.)`,
        );
        return;
      }
    }

    const contentValue = editor?.getHTML() || '';

    const payload: RequestCreateRowenaContent = {
      name: formik.values.name || '',
      pageId: formik.values.page?.value.toString() || '',
      pageType: formik.values.pageType?.value?.toString() || '',
      content: contentValue,
      variables: mergeApiVariablesWithExisting(null, availableVariables?.data.groups),
    };
    createRowenaContent(payload, {
      onSuccess: () => {
        toast.success('Rowena content created successfully!');
        formik.resetForm();
        navigate(PATH_CONSTANT.SYSTEM.ROWENA.CONTENT_DEFINITION);
      },
      onError: (error) => {
        toast.error(`Error creating Rowena content: ${error.message}`);
        console.error('Error creating Rowena content:', error);
      },
    });
  };

  const handleOnCancel = () => {
    navigate(PATH_CONSTANT.SYSTEM.ROWENA.CONTENT_DEFINITION);
  };

  const isAllFormValid = createRowenaCotnentValidationSchema.isValidSync(formik.values);
  const isLoadingOptionsData = isLoadingCubeOptions;

  const handlePageTypeChange = (e: React.SyntheticEvent, value: AutoCompleteItem | null) => {
    formik.setFieldValue('pageType', value);
  };

  return (
    <MainLayoutComponent pageTitle='Add New Dialog' breadcrumbs={[t('SIDEBAR_MENU.DASHBOARD'), 'Dialog Definition']}>
      <Box p={2} bgcolor='#fff'>
        {isLoadingOptionsData ? (
          <Stack height={450}>
            <ActivityIndicator />
          </Stack>
        ) : (
          <>
            <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
              Rowena Dialog Definition
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Stack width='100%' direction='row' gap={3} mb={5} flexWrap='wrap' alignItems='center'>
              <Stack width='50%' gap={2}>
                <Stack width='100%'>
                  <Typography variant='input-label' fontWeight={800}>
                    Dialog Name
                  </Typography>
                  <Typography variant='input-label-gray' fontSize={13}></Typography>
                  <TextField
                    sx={textInputStyles}
                    name='name'
                    disabled={isInserting}
                    hiddenLabel
                    value={formik.values.name}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    variant='outlined'
                    size='small'
                    placeholder='e.g. Utilisation Guide'
                  />
                </Stack>
                <Stack width='100%'>
                  <Typography variant='input-label' fontWeight={800}>
                    Page Type
                  </Typography>
                  <Typography variant='input-label-gray' fontSize={13}>
                    Choose the page type for which you want to create the content.
                  </Typography>
                  <Autocomplete
                    options={rowenaPageTypeOptions?.data ?? []}
                    getOptionLabel={(option) => option.label}
                    sx={{ ...autocompleteStyles, width: '100%' }}
                    clearIcon={null}
                    value={formik.values.pageType}
                    disabled={isInserting || isLoadingCubeOptions}
                    onBlur={formik.handleBlur}
                    onChange={handlePageTypeChange}
                    renderInput={(params) => (
                      <TextField {...params} name='pageType' placeholder='Select Page Type...' />
                    )}
                  />
                </Stack>
                <Stack width='100%'>
                  <Typography variant='input-label' fontWeight={800}>
                    Page
                  </Typography>
                  <Typography variant='input-label-gray' fontSize={13}>
                    Choose the page for which you want to create the content.
                  </Typography>
                  <Autocomplete
                    disabled={!formik.values.pageType || isLoadingCubeLayerOptions || isInserting}
                    options={rowenaPageOptions?.data ?? []}
                    value={formik.values.page}
                    onBlur={formik.handleBlur}
                    onChange={(e, value) => {
                      formik.setFieldValue('page', value);
                    }}
                    sx={{ ...autocompleteStyles, width: '100%' }}
                    clearIcon={null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name='page'
                        placeholder={isLoadingCubeLayerOptions ? 'Loading...' : 'Select Page...'}
                        error={formik.touched.page && Boolean(formik.errors.page)}
                        helperText={formik.touched.page && formik.errors.page}
                      />
                    )}
                  />
                </Stack>
              </Stack>
              <Stack width='100%'>
                <Typography variant='input-label' fontWeight={800}>
                  Text Content
                </Typography>
                <Typography variant='input-label-gray' fontSize={13}>
                  Write the content that you want to display on the page.
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    border: '1px dashed #E2E8F0',
                    borderRadius: 1,
                    maxWidth: '80%',
                  }}
                >
                  <Stack direction='column'>
                    <Typography variant='input-label' fontWeight={700} fontSize={13}>
                      Start from a template
                    </Typography>
                    <Typography variant='input-label-gray' fontSize={12}>
                      Pre-fills the editor with a starting structure. Selecting a template replaces the current content
                      (use undo to revert).
                    </Typography>
                  </Stack>
                  <RadioGroup
                    value={selectedTemplate}
                    onChange={(_, value) => applyTemplate(value as TemplateKey)}
                    sx={{ mt: 0.5 }}
                  >
                    {(Object.keys(CONTENT_TEMPLATES) as TemplateKey[]).map((key) => (
                      <FormControlLabel
                        key={key}
                        value={key}
                        disabled={isInserting}
                        control={<Radio size='small' />}
                        label={
                          <Box>
                            <Typography variant='body2' fontWeight={600}>
                              {CONTENT_TEMPLATES[key].label}
                            </Typography>
                            <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                              {CONTENT_TEMPLATES[key].description}
                            </Typography>
                          </Box>
                        }
                        sx={{ alignItems: 'flex-start', mb: 0.5, '& .MuiRadio-root': { pt: 0.5 } }}
                      />
                    ))}
                  </RadioGroup>
                </Box>
                <VariableTree
                  groups={availableVariables?.data.groups ?? []}
                  onInsertVariable={insertVariable}
                  disabled={isInserting}
                  isLoading={isLoadingAvailableVariables}
                  pageTypeSelected={!!pageTypeValue}
                />
                <Box sx={{ p: 1, maxWidth: '80%', mt: 1 }}>
                  <Editor ref={editorRef} content='' variables={variablesByName} />
                </Box>
              </Stack>
            </Stack>

            <Stack direction='row' px={2} mt={10} justifyContent='space-between' alignItems='center'>
              <Stack />
              <Stack direction='row' gap={2}>
                <ButtonLoading variant='main-table-panel-border' onClick={handleOnCancel}>
                  Cancel
                </ButtonLoading>
                <ButtonLoading
                  variant='main-table-panel'
                  onClick={handleOnSubmit}
                  disabled={!isAllFormValid}
                  loading={isInserting}
                >
                  Save
                </ButtonLoading>
              </Stack>
            </Stack>
          </>
        )}
      </Box>
    </MainLayoutComponent>
  );
}
